import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar2Week,
  CheckCircle,
  Clock,
  Eye,
  Gear,
  Lock,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuthContext } from "../../hooks/useAuthContext";
import "./InitialSetUp.css";

const defaultSetup = {
  mondayAvailable: true,
  mondayStartTime: "09:00",
  mondayEndTime: "17:00",

  tuesdayAvailable: true,
  tuesdayStartTime: "09:00",
  tuesdayEndTime: "17:00",

  wednesdayAvailable: true,
  wednesdayStartTime: "09:00",
  wednesdayEndTime: "17:00",

  thursdayAvailable: true,
  thursdayStartTime: "09:00",
  thursdayEndTime: "17:00",

  fridayAvailable: true,
  fridayStartTime: "09:00",
  fridayEndTime: "17:00",

  saturdayAvailable: false,
  saturdayStartTime: "",
  saturdayEndTime: "",

  sundayAvailable: false,
  sundayStartTime: "",
  sundayEndTime: "",

  daysInAdvance: "30",
  minimumNoticeRequired: "24",

  actualSessionTime: "50",
  bufferTime: "10",

  publicAvailabilityEnabled: false,
};

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const steps = [
  {
    key: "availability",
    title: "Availability",
    icon: <Calendar2Week />,
  },
  {
    key: "rules",
    title: "Booking rules",
    icon: <Gear />,
  },
  {
    key: "length",
    title: "Session length",
    icon: <Clock />,
  },
  {
    key: "privacy",
    title: "Privacy PIN",
    icon: <Lock />,
  },
  {
    key: "public",
    title: "Public page",
    icon: <Eye />,
  },
];

export default function InitialSetUp({ onComplete }) {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [setup, setSetup] = useState(defaultSetup);
  const [currentStep, setCurrentStep] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [err, setErr] = useState("");

  const [privacyPin, setPrivacyPin] = useState("");
  const [confirmPrivacyPin, setConfirmPrivacyPin] = useState("");
  const [showPrivacyPin, setShowPrivacyPin] = useState(false);

  const actualSessionTime = Number(setup.actualSessionTime);
  const bufferTime = Number(setup.bufferTime);
  const diarySlotTime = actualSessionTime + bufferTime;

  useEffect(() => {
    if (user?.token) {
      getInitialSetUp();
    }
  }, [user?.token]);

  const formatTime = (time) => {
    if (!time) return "";
    return time.substring(0, 5);
  };

  const getInitialSetUp = async () => {
    try {
      setIsLoading(true);
      setErr("");

      const response = await axios.get(
        `${ApiConnectionString}/api/InitialSetUp`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.data) {
        const data = response.data;

        const sessionDefaultTime =
          data.sessionDefaultTime || data.SessionDefaultTime || 60;

        const actualTime =
          data.actualSessionTime || data.ActualSessionTime || 50;

        const currentBufferTime = Math.max(
          sessionDefaultTime - actualTime,
          0
        );

        setSetup({
          mondayAvailable:
            data.mondayAvailable ?? data.MondayAvailable ?? true,
          mondayStartTime:
            formatTime(data.mondayStartTime || data.MondayStartTime) ||
            "09:00",
          mondayEndTime:
            formatTime(data.mondayEndTime || data.MondayEndTime) ||
            "17:00",

          tuesdayAvailable:
            data.tuesdayAvailable ?? data.TuesdayAvailable ?? true,
          tuesdayStartTime:
            formatTime(data.tuesdayStartTime || data.TuesdayStartTime) ||
            "09:00",
          tuesdayEndTime:
            formatTime(data.tuesdayEndTime || data.TuesdayEndTime) ||
            "17:00",

          wednesdayAvailable:
            data.wednesdayAvailable ?? data.WednesdayAvailable ?? true,
          wednesdayStartTime:
            formatTime(
              data.wednesdayStartTime || data.WednesdayStartTime
            ) || "09:00",
          wednesdayEndTime:
            formatTime(
              data.wednesdayEndTime || data.WednesdayEndTime
            ) || "17:00",

          thursdayAvailable:
            data.thursdayAvailable ?? data.ThursdayAvailable ?? true,
          thursdayStartTime:
            formatTime(data.thursdayStartTime || data.ThursdayStartTime) ||
            "09:00",
          thursdayEndTime:
            formatTime(data.thursdayEndTime || data.ThursdayEndTime) ||
            "17:00",

          fridayAvailable:
            data.fridayAvailable ?? data.FridayAvailable ?? true,
          fridayStartTime:
            formatTime(data.fridayStartTime || data.FridayStartTime) ||
            "09:00",
          fridayEndTime:
            formatTime(data.fridayEndTime || data.FridayEndTime) ||
            "17:00",

          saturdayAvailable:
            data.saturdayAvailable ?? data.SaturdayAvailable ?? false,
          saturdayStartTime:
            formatTime(data.saturdayStartTime || data.SaturdayStartTime) ||
            "",
          saturdayEndTime:
            formatTime(data.saturdayEndTime || data.SaturdayEndTime) ||
            "",

          sundayAvailable:
            data.sundayAvailable ?? data.SundayAvailable ?? false,
          sundayStartTime:
            formatTime(data.sundayStartTime || data.SundayStartTime) ||
            "",
          sundayEndTime:
            formatTime(data.sundayEndTime || data.SundayEndTime) ||
            "",

          daysInAdvance: String(
            data.daysInAdvance || data.DaysInAdvance || 30
          ),

          minimumNoticeRequired: String(
            data.minimumNoticeRequired ||
              data.MinimumNoticeRequired ||
              24
          ),

          actualSessionTime: String(actualTime),
          bufferTime: String(currentBufferTime),

          publicAvailabilityEnabled:
            data.publicAvailabilityEnabled ??
            data.PublicAvailabilityEnabled ??
            false,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSetup((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      if (field.includes("Available") && value === true) {
        const day = field.replace("Available", "");

        const startKey = `${day}StartTime`;
        const endKey = `${day}EndTime`;

        if (!updated[startKey]) {
          updated[startKey] = "09:00";
        }

        if (!updated[endKey]) {
          updated[endKey] = "17:00";
        }
      }

      if (field.includes("Available") && value === false) {
        const day = field.replace("Available", "");

        updated[`${day}StartTime`] = "";
        updated[`${day}EndTime`] = "";
      }

      return updated;
    });
  };

  const buildPayload = () => {
    const payload = {};

    days.forEach((day) => {
      const keyDay = day.toLowerCase();

      const availableKey = `${keyDay}Available`;
      const startKey = `${keyDay}StartTime`;
      const endKey = `${keyDay}EndTime`;

      const isAvailable = setup[availableKey];

      payload[availableKey] = isAvailable;

      payload[startKey] = isAvailable
        ? `${setup[startKey]}:00`
        : null;

      payload[endKey] = isAvailable
        ? `${setup[endKey]}:00`
        : null;
    });

    payload.daysInAdvance = Number(setup.daysInAdvance);

    payload.minimumNoticeRequired = Number(
      setup.minimumNoticeRequired
    );

    payload.actualSessionTime = Number(setup.actualSessionTime);

    payload.sessionDefaultTime =
      Number(setup.actualSessionTime) +
      Number(setup.bufferTime);

    payload.publicAvailabilityEnabled =
      setup.publicAvailabilityEnabled;

    return payload;
  };

  const hasAtLeastOneAvailableDay = () => {
    return days.some((day) => {
      const keyDay = day.toLowerCase();

      return setup[`${keyDay}Available`];
    });
  };

  const hasInvalidTimes = () => {
    return days.some((day) => {
      const keyDay = day.toLowerCase();

      const availableKey = `${keyDay}Available`;
      const startKey = `${keyDay}StartTime`;
      const endKey = `${keyDay}EndTime`;

      if (!setup[availableKey]) {
        return false;
      }

      if (!setup[startKey] || !setup[endKey]) {
        return true;
      }

      return setup[endKey] <= setup[startKey];
    });
  };

  const validateCurrentStep = () => {
    setErr("");

    if (currentStep === 0) {
      if (!hasAtLeastOneAvailableDay()) {
        setErr(
          "Please choose at least one day you are available."
        );

        return false;
      }

      if (hasInvalidTimes()) {
        setErr(
          "Please check your start and end times."
        );

        return false;
      }
    }

    if (currentStep === 1) {
      if (
        !Number(setup.daysInAdvance) ||
        Number(setup.daysInAdvance) < 1
      ) {
        setErr(
          "Please enter how many days in advance clients can book."
        );

        return false;
      }

      if (Number(setup.minimumNoticeRequired) < 0) {
        setErr(
          "Minimum notice cannot be less than 0."
        );

        return false;
      }
    }

    if (currentStep === 2) {
      if (
        !actualSessionTime ||
        actualSessionTime <= 0
      ) {
        setErr(
          "Please choose a valid session length."
        );

        return false;
      }

      if (bufferTime < 0) {
        setErr(
          "Buffer time cannot be less than 0."
        );

        return false;
      }

      if (diarySlotTime <= 0) {
        setErr(
          "Please choose valid appointment timings."
        );

        return false;
      }
    }

    if (currentStep === 3) {
      if (!/^\d{4}$/.test(privacyPin)) {
        setErr(
          "Please choose a 4-digit privacy PIN."
        );

        return false;
      }

      if (privacyPin !== confirmPrivacyPin) {
        setErr(
          "The privacy PINs do not match."
        );

        return false;
      }
    }

    return true;
  };

  const goNext = () => {
    setValidated(true);

    if (!validateCurrentStep()) {
      return;
    }

    setValidated(false);
    setErr("");

    setCurrentStep((prev) => {
      return Math.min(
        prev + 1,
        steps.length - 1
      );
    });
  };

  const goBack = () => {
    setErr("");
    setValidated(false);

    setCurrentStep((prev) => {
      return Math.max(
        prev - 1,
        0
      );
    });
  };

  const submit = async () => {
    if (currentStep !== steps.length - 1) {
      return;
    }

    setValidated(true);
    setErr("");

    if (!user?.token) {
      setErr(
        "You must be logged in to complete setup."
      );

      return;
    }

    if (!/^\d{4}$/.test(privacyPin)) {
      setErr("Please enter a 4-digit privacy PIN.");
      setCurrentStep(3);
      return;
    }

    if (privacyPin !== confirmPrivacyPin) {
      setErr("The privacy PINs do not match.");
      setCurrentStep(3);
      return;
    }

    if (!validateCurrentStep()) {
      return;
    }

    try {
      setIsLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/privacy-pin/set`,
        {
          pin: privacyPin,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const payload = buildPayload();

      const response = await axios.put(
        `${ApiConnectionString}/api/InitialSetUp`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.status === 200) {
        try {
          if (onComplete) {
            await onComplete();
          }
        } catch (error) {
          console.error(error);
        }

        navigate("/appointments");
      }
    } catch (error) {
      console.error(error);

      const message =
        typeof error.response?.data === "string"
          ? error.response.data
          : "Failed to complete setup.";

      setErr(message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderAvailabilityStep = () => {
    return (
      <section className="setup-panel">
        <div className="setup-section-header">
          <h2>
            When are you usually available?
          </h2>

          <p>
            Choose the days and times clients can book
            appointments with you.
          </p>
        </div>

        <div className="setup-day-list">
          {days.map((day) => {
            const keyDay = day.toLowerCase();

            const availableKey =
              `${keyDay}Available`;

            const startKey =
              `${keyDay}StartTime`;

            const endKey =
              `${keyDay}EndTime`;

            return (
              <div
                className="setup-day-row"
                key={day}
              >
                <div className="setup-day-title">
                  <strong>
                    {day}
                  </strong>

                  <span>
                    {setup[availableKey]
                      ? "Available"
                      : "Closed"}
                  </span>
                </div>

                <label className="setup-toggle">
                  <input
                    type="checkbox"
                    checked={
                      setup[availableKey]
                    }
                    onChange={(e) =>
                      updateField(
                        availableKey,
                        e.target.checked
                      )
                    }
                  />

                  <span></span>
                </label>

                <div className="setup-time-fields">
                  <input
                    type="time"
                    value={
                      setup[startKey]
                    }
                    disabled={
                      !setup[availableKey]
                    }
                    required={
                      setup[availableKey]
                    }
                    onChange={(e) =>
                      updateField(
                        startKey,
                        e.target.value
                      )
                    }
                  />

                  <span>
                    to
                  </span>

                  <input
                    type="time"
                    value={
                      setup[endKey]
                    }
                    disabled={
                      !setup[availableKey]
                    }
                    required={
                      setup[availableKey]
                    }
                    onChange={(e) =>
                      updateField(
                        endKey,
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const renderRulesStep = () => {
    return (
      <section className="setup-panel">
        <div className="setup-section-header">
          <h2>
            How should bookings work?
          </h2>

          <p>
            Set how far ahead clients can book and how
            much notice you need.
          </p>
        </div>

        <div className="setup-choice-list">
          <div className="setup-choice-card">
            <div>
              <strong>
                Book ahead
              </strong>

              <span>
                How far into the future clients can book.
              </span>
            </div>

            <div className="setup-number-field">
              <input
                type="number"
                min="1"
                max="365"
                value={
                  setup.daysInAdvance
                }
                onChange={(e) =>
                  updateField(
                    "daysInAdvance",
                    e.target.value
                  )
                }
              />

              <span>
                days
              </span>
            </div>
          </div>

          <div className="setup-choice-card">
            <div>
              <strong>
                Minimum notice
              </strong>

              <span>
                How much warning you need before a booking.
              </span>
            </div>

            <div className="setup-number-field">
              <input
                type="number"
                min="0"
                max="168"
                value={
                  setup.minimumNoticeRequired
                }
                onChange={(e) =>
                  updateField(
                    "minimumNoticeRequired",
                    e.target.value
                  )
                }
              />

              <span>
                hours
              </span>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderLengthStep = () => {
    return (
      <section className="setup-panel">
        <div className="setup-section-header">
          <h2>
            How long are your appointments?
          </h2>

          <p>
            Choose the client session length and any time
            you want kept free afterwards.
          </p>
        </div>

        <div className="setup-choice-list">
          <div className="setup-choice-card">
            <div>
              <strong>
                Session length
              </strong>

              <span>
                How long the client session lasts.
              </span>
            </div>

            <select
              value={
                setup.actualSessionTime
              }
              onChange={(e) =>
                updateField(
                  "actualSessionTime",
                  e.target.value
                )
              }
            >
              <option value="30">
                30 minutes
              </option>

              <option value="45">
                45 minutes
              </option>

              <option value="50">
                50 minutes
              </option>

              <option value="60">
                60 minutes
              </option>

              <option value="75">
                75 minutes
              </option>

              <option value="90">
                90 minutes
              </option>
            </select>
          </div>

          <div className="setup-choice-card">
            <div>
              <strong>
                Buffer time
              </strong>

              <span>
                Time for notes, admin, overruns or a short
                break.
              </span>
            </div>

            <select
              value={
                setup.bufferTime
              }
              onChange={(e) =>
                updateField(
                  "bufferTime",
                  e.target.value
                )
              }
            >
              <option value="0">
                No buffer
              </option>

              <option value="5">
                5 minutes
              </option>

              <option value="10">
                10 minutes
              </option>

              <option value="15">
                15 minutes
              </option>

              <option value="20">
                20 minutes
              </option>

              <option value="25">
                25 minutes
              </option>

              <option value="30">
                30 minutes
              </option>
            </select>
          </div>

          <div className="setup-summary-box">
            <strong>
              {actualSessionTime || 0} min session +{" "}
              {bufferTime || 0} min buffer
            </strong>

            <span>
              {diarySlotTime || 0} minutes reserved in your
              diary.
            </span>
          </div>
        </div>
      </section>
    );
  };

  const renderPrivacyStep = () => {
    return (
      <section className="setup-panel">
        <div className="setup-section-header">
          <h2>
            Create your privacy PIN
          </h2>

          <p>
            Your PIN protects sensitive client details. Until it is entered,
            client surnames are shortened and email addresses are hidden.
            Unlocking lasts for 1 hour.
          </p>
        </div>

        <div className="setup-choice-list">
          <div className="setup-choice-card">
            <div>
              <strong>
                4-digit PIN
              </strong>

              <span>
                Choose a PIN you can remember.
              </span>
            </div>

            <input
              type={showPrivacyPin ? "text" : "password"}
              className="form-control"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="new-password"
              maxLength={4}
              value={privacyPin}
              onChange={(e) =>
                setPrivacyPin(
                  e.target.value.replace(/[^0-9]/g, "").slice(0, 4)
                )
              }
              placeholder="••••"
              required
            />
          </div>

          <div className="setup-choice-card">
            <div>
              <strong>
                Confirm PIN
              </strong>

              <span>
                Enter the same 4 digits again.
              </span>
            </div>

            <input
              type={showPrivacyPin ? "text" : "password"}
              className="form-control"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="new-password"
              maxLength={4}
              value={confirmPrivacyPin}
              onChange={(e) =>
                setConfirmPrivacyPin(
                  e.target.value.replace(/[^0-9]/g, "").slice(0, 4)
                )
              }
              placeholder="••••"
              required
            />
          </div>

          <button
            type="button"
            className="setup-secondary-btn"
            onClick={() =>
              setShowPrivacyPin((prev) => !prev)
            }
          >
            {showPrivacyPin ? "Hide PIN" : "Show PIN"}
          </button>

          <div className="setup-summary-box">
            <strong>
              Extra privacy for client information
            </strong>

            <span>
              You can change your PIN later in Settings.
            </span>
          </div>
        </div>
      </section>
    );
  };

  const renderPublicStep = () => {
    return (
      <section className="setup-panel">
        <div className="setup-section-header">
          <h2>
            Do you want a public availability page?
          </h2>

          <p>
            People can view your general availability without logging in.
            You can turn this off later.
          </p>
        </div>

        <div className="setup-public-card">
          <div>
            <strong>
              Public availability page
            </strong>

            <span>
              This only shows general availability ranges. It does not show
              client names, private appointments, notes, or diary details.
            </span>

            <div
              className={`mt-2 fw-bold ${
                setup.publicAvailabilityEnabled
                  ? "text-success"
                  : "text-secondary"
              }`}
            >
              Currently{" "}
              {setup.publicAvailabilityEnabled ? "ON" : "OFF"}
            </div>
          </div>

          <label className="setup-toggle">
            <input
              type="checkbox"
              checked={setup.publicAvailabilityEnabled}
              onChange={(e) =>
                updateField(
                  "publicAvailabilityEnabled",
                  e.target.checked
                )
              }
            />

            <span></span>
          </label>
        </div>

        <div className="setup-final-note">
          <CheckCircle />

          <div>
            <strong>
              You’re nearly done
            </strong>

            <span>
              After this, you’ll be taken to your diary where you can start
              adding clients and appointments.
            </span>
          </div>
        </div>
      </section>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep === 0) {
      return renderAvailabilityStep();
    }

    if (currentStep === 1) {
      return renderRulesStep();
    }

    if (currentStep === 2) {
      return renderLengthStep();
    }

    if (currentStep === 3) {
      return renderPrivacyStep();
    }

    if (currentStep === 4) {
      return renderPublicStep();
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="initial-setup-loading">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="initial-setup-page">
      <div className="initial-setup-shell">

        <div className="setup-header">
          <span>
            Welcome
          </span>

          <h1>
            Set up your practice
          </h1>

          <p>
            A few quick steps to get your diary ready. You
            can change these settings later.
          </p>
        </div>

        <div className="setup-progress">
          {steps.map((step, index) => (
            <div
              key={step.key}
              className={`setup-progress-step ${
                index === currentStep
                  ? "active"
                  : ""
              } ${
                index < currentStep
                  ? "complete"
                  : ""
              }`}
            >
              <div className="setup-progress-icon">
                {index < currentStep
                  ? <CheckCircle />
                  : step.icon}
              </div>

              <span>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {err && (
          <div className="alert alert-danger">
            {err}
          </div>
        )}

        <div
          className={
            validated
              ? "was-validated"
              : ""
          }
        >
          {renderCurrentStep()}

          <div className="setup-actions">
            {currentStep > 0 && (
              <button
                type="button"
                className="setup-secondary-btn"
                onClick={goBack}
              >
                Back
              </button>
            )}

            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                className="setup-primary-btn"
                onClick={goNext}
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                className="setup-primary-btn"
                onClick={submit}
              >
                Finish setup
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}